import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { authApi, authStore } from "@/lib/apiClient";
import { AUTH } from "@/constants/testIds";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const tokens = await authApi.login(email, password);
      authStore.set(tokens);
      nav("/admin", { replace: true });
    } catch (err) {
      const code = err.response?.status;
      const msg =
        code === 423
          ? "Your account is temporarily locked. Please try again later."
          : code === 429
            ? "Too many attempts. Please wait a few minutes."
            : err.response?.data?.error?.message || "Sign-in failed.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-100 px-4"
      data-testid="AdminLogin_41_4"
    >
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-start">
          <Link
            to="/"
            className="text-sm text-neutral-400 hover:text-neutral-100"
            data-testid="admin-login-back"
          >
            ← Back to store
          </Link>
        </div>
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold text-neutral-50">Admin sign-in</h1>
        </div>
        <Card className="border border-neutral-800 bg-neutral-900 text-neutral-100 shadow-2xl shadow-black/40">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl text-neutral-50">Welcome back</CardTitle>
            <CardDescription className="text-neutral-400">
              Sign in with your work email.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={onSubmit}
              className="space-y-4"
              data-testid={AUTH.loginForm}
              noValidate
            >
              {error && (
                <Alert
                  variant="destructive"
                  className="border-red-900/60 bg-red-950/40 text-red-200"
                  data-testid={AUTH.errorAlert}
                >
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-neutral-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-neutral-950 border-neutral-800 text-neutral-100 placeholder:text-neutral-600 focus-visible:ring-neutral-500"
                  data-testid={AUTH.emailInput}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-neutral-300">Password</Label>
                  <Link
                    to="/admin/password-reset"
                    className="text-sm text-neutral-500 hover:text-neutral-200"
                    data-testid={AUTH.forgotLink}
                  >
                    Forgot?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-neutral-950 border-neutral-800 text-neutral-100 placeholder:text-neutral-600 focus-visible:ring-neutral-500"
                  data-testid={AUTH.passwordInput}
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-neutral-50 text-neutral-950 hover:bg-neutral-200"
                disabled={busy}
                data-testid={AUTH.submitBtn}
              >
                {busy ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-neutral-500 mt-6">
          Employee access only. All activity is logged.
        </p>
      </div>
    </div>
  );
}
